import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signupUserSchema, type SignupUserInput, COGNITO_GROUPS } from '@parecer/shared';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cognitoSignUp, mapCognitoErrorMessage } from '@/lib/cognito';
import { useToast } from '@/components/ui/use-toast';

export function Cadastro() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<SignupUserInput>({
    resolver: zodResolver(signupUserSchema),
    defaultValues: { email: '', password: '', name: '', phone: undefined },
  });

  async function onSubmit(values: SignupUserInput) {
    setLoading(true);
    try {
      await cognitoSignUp({
        email: values.email,
        password: values.password,
        attributes: {
          email: values.email,
          given_name: values.name,
          'custom:role': COGNITO_GROUPS.USERS,
          ...(values.phone ? { phone_number: values.phone } : {}),
        },
      });
      toast({ variant: 'success', title: 'Conta criada', description: 'Confirme seu email com o código enviado.' });
      navigate(`/confirmar?email=${encodeURIComponent(values.email)}`);
    } catch (err) {
      toast({ variant: 'danger', title: 'Falha no cadastro', description: mapCognitoErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-md py-16">
        <Card>
          <CardHeader>
            <CardTitle>Criar conta</CardTitle>
            <CardDescription>Suba contratos, receba pré-análise IA e contrate advogados.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome completo</Label>
                <Input id="name" autoComplete="name" className="mt-1.5" {...form.register('name')} />
                {form.formState.errors.name && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.name.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="email" className="mt-1.5" {...form.register('email')} />
                {form.formState.errors.email && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone">Celular (opcional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+5511999999999"
                  className="mt-1.5"
                  {...form.register('phone')}
                />
                {form.formState.errors.phone && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.phone.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  className="mt-1.5"
                  {...form.register('password')}
                />
                {form.formState.errors.password && (
                  <p className="text-xs text-danger mt-1">{form.formState.errors.password.message}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Mínimo 8 caracteres, com maiúscula, minúscula e número.
                </p>
              </div>
              <Button type="submit" variant="accent" className="w-full" loading={loading}>
                Criar conta
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Ao criar conta você concorda com nossos termos de uso e política de privacidade.
              </p>
            </form>
            <p className="mt-6 text-sm text-center text-muted-foreground">
              Já tem conta?{' '}
              <Link to="/login" className="text-foreground font-medium hover:underline">
                Entrar
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </MarketingLayout>
  );
}
